import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Trash2, Plus, Minus, ShoppingBag, Copy, Check, ChevronLeft, MessageCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { TELEGRAM_URL } from '../data/mock';
import { createOrder } from '../lib/api';
import { openSessionApp } from '../lib/sessionApp';
import CaliLetters from '../components/CaliLetters';

const ZANGI_NUMBER = '3166750580';

const Checkout = () => {
  const { items, updateQty, removeItem, total, clearCart } = useCart();
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [copied, setCopied] = useState(false);
  const [sessionCopied, setSessionCopied] = useState(false);

  const buildMessage = () => {
    let msg = 'Cześć Cali Boyz! Chcę złożyć zamówienie:\n\n';
    items.forEach((i, idx) => {
      msg += `${idx + 1}. ${i.name} (${i.thc}) x${i.qty} — ${(i.price * i.qty).toFixed(2)} zł\n`;
    });
    msg += `\nRAZEM: ${total.toFixed(2)} zł`;
    if (name.trim()) msg += `\n\nImię: ${name}`;
    if (note.trim()) msg += `\nUwagi: ${note}`;
    return msg;
  };

  const saveOrder = async () => {
    try {
      await createOrder({
        items: items.map((i) => ({ id: i.id, name: i.name, price: i.price, thc: i.thc || '', qty: i.qty })),
        total,
        name,
        note,
      });
    } catch { /* ignore, still allow telegram */ }
  };

  const handleTelegram = async () => {
    await saveOrder();
    window.open(TELEGRAM_URL, '_blank');
  };

  const handleExternalOrder = async (url) => {
    await saveOrder();
    window.open(url, '_blank');
  };

  const handleSession = async () => {
    await saveOrder();
    const copiedId = await openSessionApp();
    if (copiedId) {
      setSessionCopied(true);
      setTimeout(() => setSessionCopied(false), 2200);
    }
  };

  const handleCopy = async () => {
    await saveOrder();
    try {
      await navigator.clipboard.writeText(buildMessage());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  if (items.length === 0) {
    return (
      <div className="pt-[140px] text-center min-h-[60vh]">
        <ShoppingBag size={48} className="mx-auto mb-4 text-white/30" />
        <p className="text-white/60">Twój koszyk jest pusty.</p>
        <Link to="/produkty" className="btn-green inline-block mt-4 px-6 py-2">Zobacz produkty</Link>
      </div>
    );
  }

  return (
    <div className="pt-[100px]">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8">
        <Link to="/produkty" className="inline-flex items-center gap-1 text-white/60 hover:text-[#a6e22e] text-sm mb-6"><ChevronLeft size={16} /> Kontynuuj zakupy</Link>
        <h1 className="font-anton text-3xl lg:text-4xl uppercase mb-8">
          <CaliLetters text="TWOJE ZAMÓWIENIE" />
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Items */}
          <div className="lg:col-span-2 space-y-3">
            {items.map((i) => (
              <div key={i.id} className="flex gap-4 cb-panel rounded-2xl border border-white/10 p-4">
                <img src={i.image} alt={i.name} className="h-20 w-20 object-contain rounded-lg bg-black/40" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">{i.name}</p>
                  <p className="text-white/40 text-xs mb-2">{i.thc}</p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQty(i.id, i.qty - 1)} className="h-7 w-7 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"><Minus size={13} /></button>
                    <span className="w-8 text-center">{i.qty}</span>
                    <button onClick={() => updateQty(i.id, i.qty + 1)} className="h-7 w-7 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"><Plus size={13} /></button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[#a6e22e] font-bold">{(i.price * i.qty).toFixed(2)} zł</p>
                  <button onClick={() => removeItem(i.id)} className="text-white/40 hover:text-[#ff2e2e] transition mt-2"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}

            <div className="cb-panel rounded-2xl border border-white/10 p-5 space-y-3">
              <p className="font-anton uppercase text-sm text-[#a6e22e]">Dane do zamówienia (opcjonalne)</p>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Imię / nick" className="w-full rounded-xl bg-black/60 border border-white/15 px-4 py-3 text-sm outline-none focus:border-[#a6e22e] transition" />
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Uwagi do zamówienia" className="w-full rounded-xl bg-black/60 border border-white/15 px-4 py-3 text-sm outline-none focus:border-[#a6e22e] transition resize-none" />
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="cb-panel rounded-2xl border border-white/10 p-6 sticky top-24">
              <h2 className="font-anton uppercase text-lg mb-4">Podsumowanie</h2>
              <div className="flex justify-between text-white/70 text-sm mb-2"><span>Produkty ({items.length})</span><span>{total.toFixed(2)} zł</span></div>
              <div className="flex justify-between text-white/70 text-sm mb-4"><span>Dostawa</span><span>ustalana na Telegramie</span></div>
              <div className="flex justify-between text-xl font-bold border-t border-white/10 pt-4 mb-5"><span>Razem</span><span className="text-[#a6e22e]">{total.toFixed(2)} zł</span></div>

              <button onClick={() => handleExternalOrder(`https://services.zangi.com/dl/conversation/${ZANGI_NUMBER}`)} className="btn-green w-full py-3.5 flex items-center justify-center gap-2 mb-3"><Send size={18} /> Zamów na Zangi</button>
              <button onClick={handleSession} className="btn-ghost-red w-full py-3.5 flex items-center justify-center gap-2 mb-3"><MessageCircle size={18} /> {sessionCopied ? 'ID skopiowane' : 'Zamów na Session'}</button>
              <button onClick={handleTelegram} className="btn-ghost-red w-full py-3.5 flex items-center justify-center gap-2 mb-3"><Send size={18} /> Dołącz do grupy na Telegramie</button>
              <button onClick={handleCopy} className="btn-ghost-red w-full py-3 flex items-center justify-center gap-2">
                {copied ? <><Check size={16} /> Skopiowano!</> : <><Copy size={16} /> Kopiuj treść zamówienia</>}
              </button>
              <p className="text-white/40 text-xs text-center mt-3">Skopiuj wiadomość i wklej ją na naszym czacie Telegram, aby sfinalizować zakup.</p>
              <button onClick={clearCart} className="text-white/40 hover:text-[#ff2e2e] text-xs mt-4 w-full text-center transition">Wyczyść koszyk</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
